import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Wrench, Plus, Edit, Trash2, Search, Clock, DollarSign, X, Check,
  AlertCircle, Calendar, FileText, TrendingUp
} from 'lucide-react';
import { apiService, MaintenanceRecord, MaintenanceSummary } from '../../services/api';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface MaintenanceFormData {
  vehicleId: string;
  date: string;
  type: 'preventive' | 'corrective' | 'inspection' | 'recall';
  description: string;
  cost: string;
  provider: string;
}

const emptyForm: MaintenanceFormData = {
  vehicleId: '',
  date: new Date().toISOString().split('T')[0],
  type: 'preventive',
  description: '',
  cost: '',
  provider: '',
};

export const MaintenanceHistoryPage: React.FC = () => {
  const { t } = useTranslation();

  const [records, setRecords] = useState<MaintenanceRecord[]>([]);
  const [summary, setSummary] = useState<MaintenanceSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<MaintenanceFormData>(emptyForm);
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);

  const maintenanceTypes = [
    { value: 'preventive', label: 'Preventiva', color: 'text-green-700 bg-green-100' },
    { value: 'corrective', label: 'Corretiva', color: 'text-red-700 bg-red-100' },
    { value: 'inspection', label: 'Inspeção', color: 'text-blue-700 bg-blue-100' },
    { value: 'recall', label: 'Recall', color: 'text-orange-700 bg-orange-100' },
  ];

  const formatCurrency = (value: number | string | null | undefined): string => {
    const numericValue = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(numericValue)) {
      return '0,00';
    }
    return numericValue.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [recordsData, summaryData] = await Promise.all([
        apiService.getMaintenanceByVehicle(page, 50),
        apiService.getMaintenanceSummary(),
      ]);

      setRecords(Array.isArray(recordsData.data) ? recordsData.data : []);
      setTotal(recordsData.total || 0);
      setSummary(summaryData);
    } catch (err: any) {
      setError('Erro ao carregar histórico de manutenção');
      console.error('Erro ao carregar manutenções:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (record: MaintenanceRecord) => {
    setFormData({
      vehicleId: record.vehicleId,
      date: record.date.split('T')[0],
      type: record.type,
      description: record.description,
      cost: record.cost ? String(record.cost) : '',
      provider: record.provider || '',
    });
    setEditingId(record.id);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.vehicleId || !formData.description.trim()) {
      setToast({ message: 'Veículo e descrição são obrigatórios', type: 'error' });
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload = {
        date: new Date(formData.date).toISOString(),
        type: formData.type,
        description: formData.description.trim(),
        cost: formData.cost ? parseFloat(formData.cost) : undefined,
        provider: formData.provider.trim() || undefined,
      };

      if (editingId) {
        await apiService.updateMaintenance(editingId, payload);
        setToast({ message: 'Manutenção atualizada com sucesso!', type: 'success' });
      } else {
        await apiService.createMaintenance(formData.vehicleId, payload);
        setToast({ message: 'Manutenção registrada com sucesso!', type: 'success' });
      }

      await load();
      resetForm();
    } catch (err: any) {
      setToast({ message: err.message || 'Erro ao salvar manutenção', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, description: string) => {
    if (!window.confirm(`Tem certeza que deseja excluir "${description}"?`)) return;

    try {
      setSaving(true);
      await apiService.deleteMaintenance(id);
      await load();
      setToast({ message: 'Manutenção excluída com sucesso!', type: 'success' });
    } catch (err: any) {
      setToast({ message: 'Erro ao excluir manutenção', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const getTypeInfo = (type: string) => {
    return maintenanceTypes.find(t => t.value === type) || maintenanceTypes[0];
  };

  const filteredRecords = records.filter(record => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      record.description.toLowerCase().includes(search) ||
      record.provider?.toLowerCase().includes(search) ||
      record.vehicle?.plate.toLowerCase().includes(search) ||
      `${record.vehicle?.brand?.name || ''} ${record.vehicle?.model?.name || ''}`.toLowerCase().includes(search)
    );
  });

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top">
          <div className={`px-4 py-3 rounded-lg shadow-lg flex items-center gap-2 ${
            toast.type === 'success' ? 'bg-green-600 text-white' :
            toast.type === 'error' ? 'bg-red-600 text-white' :
            'bg-blue-600 text-white'
          }`}>
            {toast.type === 'success' && <Check className="h-5 w-5" />}
            {toast.type === 'error' && <X className="h-5 w-5" />}
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <Wrench className="h-6 w-6" />
            Histórico de Manutenção
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Acompanhe todas as manutenções realizadas nos veículos
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 flex items-center gap-2 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Novo Registro
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}

      {/* Stats Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-bold text-foreground">{summary.total}</p>
              </div>
              <Wrench className="h-8 w-8 text-accent-600" />
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Custo Total</p>
                <p className="text-lg font-bold text-foreground">
                  R$ {formatCurrency(summary.totalCost)}
                </p>
              </div>
              <DollarSign className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Preventivas</p>
                <p className="text-2xl font-bold text-green-600">{summary.preventive}</p>
              </div>
              <Check className="h-8 w-8 text-green-600" />
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Corretivas</p>
                <p className="text-2xl font-bold text-red-600">{summary.corrective}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Inspeções</p>
                <p className="text-2xl font-bold text-blue-600">{summary.inspection}</p>
              </div>
              <Search className="h-8 w-8 text-blue-600" />
            </div>
          </div>

          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Custo Médio</p>
                <p className="text-lg font-bold text-foreground">
                  R$ {formatCurrency(summary.averageCost)}
                </p>
              </div>
              <TrendingUp className="h-8 w-8 text-purple-600" />
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Buscar por descrição, placa, veículo ou fornecedor..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-9 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-600"
        />
      </div>

      {/* Records List */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <Clock className="h-8 w-8 animate-spin mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">Carregando...</p>
          </div>
        ) : filteredRecords.length === 0 ? (
          <div className="p-8 text-center">
            <Wrench className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              {searchTerm ? 'Nenhuma manutenção encontrada' : 'Nenhuma manutenção registrada'}
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-accent/5">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Data</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Veículo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Tipo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Descrição</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Custo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase">Fornecedor</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filteredRecords.map((record) => {
                const typeInfo = getTypeInfo(record.type);
                return (
                  <tr key={record.id} className="hover:bg-accent/5">
                    <td className="px-4 py-3 text-sm text-foreground">
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {record.date ? new Date(record.date).toLocaleDateString('pt-BR') : '-'}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div>
                        <p className="font-medium text-foreground">{record.vehicle?.plate}</p>
                        <p className="text-xs text-muted-foreground">
                          {record.vehicle?.brand?.name} {record.vehicle?.model?.name}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
                        {typeInfo.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-foreground max-w-xs">
                      <p className="line-clamp-2">{record.description}</p>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-foreground">
                      {record.cost !== null && record.cost !== undefined ? `R$ ${formatCurrency(record.cost)}` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground">
                      {record.provider || '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(record)}
                          disabled={saving}
                          className="p-1.5 text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(record.id, record.description)}
                          disabled={saving}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-foreground">
                {editingId ? 'Editar Manutenção' : 'Novo Registro de Manutenção'}
              </h2>
              <button onClick={resetForm} className="text-muted-foreground hover:text-foreground">
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Veículo (ID) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={formData.vehicleId}
                    onChange={(e) => setFormData(prev => ({ ...prev, vehicleId: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-600"
                    disabled={!!editingId}
                    required
                    placeholder="UUID do veículo"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Você pode encontrar o ID na página de detalhes do veículo
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Data <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-600"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Tipo <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-600"
                  required
                >
                  {maintenanceTypes.map(type => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Descrição <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-600"
                  rows={3}
                  required
                  placeholder="Descreva a manutenção realizada..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Custo</label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="number"
                      step="0.01"
                      value={formData.cost}
                      onChange={(e) => setFormData(prev => ({ ...prev, cost: e.target.value }))}
                      className="w-full pl-9 pr-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-600"
                      placeholder="0.00"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Fornecedor</label>
                  <input
                    type="text"
                    value={formData.provider}
                    onChange={(e) => setFormData(prev => ({ ...prev, provider: e.target.value }))}
                    className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-600"
                    placeholder="Nome da oficina ou fornecedor"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 pt-4 border-t border-border">
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 disabled:opacity-50 transition-colors"
                >
                  {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Registrar'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={saving}
                  className="px-4 py-2 border border-border rounded-lg hover:bg-accent/5 transition-colors"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
